import React, { useState, useEffect } from 'react';
import {
  UserPlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  EyeIcon,
  TrashIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  LinkIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { employeeAPI, userManagementAPI, departmentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import EmployeeEditModal from "../components/EmployeeDetail/EmployeeEditModal";
import EmployeeCreateModal from "../components/EmployeeDetail/EmployeeCreateModal";
import EmployeeDetailModal from '../components/EmployeeDetail/EmployeeDetailModal';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [departments, setDepartments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // User association state
  const [showUserModal, setShowUserModal] = useState(false);
  const [userModalEmployee, setUserModalEmployee] = useState(null);
  const [userModalMode, setUserModalMode] = useState('link'); // 'link', 'create'
  const [availableUsers, setAvailableUsers] = useState([]);
  const [newUserData, setNewUserData] = useState({
    email: '',
    role: 'employee',
    sendInvite: true
  });
  const [userLoading, setUserLoading] = useState(false);
  const [userMessage, setUserMessage] = useState('');

  const { user, hasPermission } = useAuth();

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        department: selectedDepartment
      };
      
      const response = await employeeAPI.getAll(params);
      setEmployees(response.data.employees);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setError('Failed to fetch employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await departmentsAPI.getActive();
      setDepartments(response.data.departments);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  }, [currentPage, searchTerm, selectedDepartment]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchEmployees();
  };

  const openModal = (mode, employee = null) => {
    setModalMode(mode);
    setSelectedEmployee(employee);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEmployee(null);
  };

  const handleDeactivate = async (employeeId) => {
    if (!window.confirm('Are you sure you want to deactivate this employee?')) return;
    
    try {
      await employeeAPI.deactivate(employeeId, 'Admin deactivation');
      fetchEmployees();
    } catch (error) {
      console.error('Error deactivating employee:', error);
      alert('Failed to deactivate employee');
    }
  };

  // User association functions
  const openUserModal = async (employee, mode = 'link') => {
    setUserModalEmployee(employee);
    setUserModalMode(mode);
    setUserMessage('');
    setNewUserData({
      email: employee.email || employee.personal_email || '',
      role: 'employee',
      sendInvite: true
    });

    if (mode === 'link') {
      // Load available users
      try {
        const response = await employeeAPI.getUnassociatedUsers({ limit: 100 });
        setAvailableUsers(response.data.users);

        // Check for automatic email-based linking opportunity
        const employeeEmail = employee.email || employee.personal_email;
        if (employeeEmail) {
          const matchingUser = response.data.users.find(user => 
            user.email.toLowerCase() === employeeEmail.toLowerCase()
          );
          if (matchingUser) {
            setUserMessage(`Found matching user account for ${employeeEmail}. You can link it automatically.`);
          }
        }
      } catch (error) {
        console.error('Error loading users:', error);
        setUserMessage('Failed to load available users');
      }
    }

    setShowUserModal(true);
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setUserModalEmployee(null);
    setUserMessage('');
    setNewUserData({ email: '', role: 'employee', sendInvite: true });
    setAvailableUsers([]);
  };

  const handleLinkExistingUser = async (userId) => {
    setUserLoading(true);
    setUserMessage('');

    try {
      // Update employee to link with existing user
      await employeeAPI.update(userModalEmployee.id, {
        user_id: userId
      });
      
      setUserMessage('User linked successfully');
      fetchEmployees();
      
      setTimeout(() => {
        closeUserModal();
      }, 1500);
    } catch (error) {
      console.error('Error linking user:', error);
      setUserMessage(error.response?.data?.message || 'Failed to link user');
    } finally {
      setUserLoading(false);
    }
  };

  const handleCreateNewUser = async (e) => {
    e.preventDefault();
    setUserLoading(true);
    setUserMessage('');

    try {
      // Create new user
      const userResponse = await userManagementAPI.create({
        email: newUserData.email,
        first_name: userModalEmployee.first_name,
        last_name: userModalEmployee.last_name,
        role: newUserData.role,
        employee_number: userModalEmployee.employee_number,
        job_title: userModalEmployee.job_title,
        department: userModalEmployee.department
      });

      // Link the new user with the employee
      await employeeAPI.update(userModalEmployee.id, {
        user_id: userResponse.data.user.id
      });

      setUserMessage('User created and linked successfully');
      fetchEmployees();

      setTimeout(() => {
        closeUserModal();
      }, 1500);
    } catch (error) {
      console.error('Error creating user:', error);
      setUserMessage(error.response?.data?.message || 'Failed to create user');
    } finally {
      setUserLoading(false);
    }
  };

  const handleAutoLinkByEmail = async () => {
    if (!userModalEmployee) return;

    setUserLoading(true);
    setUserMessage('');

    try {
      const employeeEmail = userModalEmployee.email || userModalEmployee.personal_email;
      const response = await employeeAPI.autoLinkByEmail(userModalEmployee.id, {
        email: employeeEmail
      });
      
      setUserMessage(response.data.message);
      fetchEmployees();
      
      setTimeout(() => {
        closeUserModal();
      }, 1500);
    } catch (error) {
      console.error('Error auto-linking user:', error);
      setUserMessage(error.response?.data?.message || 'Failed to auto-link user');
    } finally {
      setUserLoading(false);
    }
  };

  const handleUnlinkUser = async (employee) => {
    if (!window.confirm(`Are you sure you want to unlink the user account from ${employee.first_name} ${employee.last_name}? They will lose access to the system.`)) {
      return;
    }

    try {
      await employeeAPI.update(employee.id, {
        user_id: null
      });
      
      fetchEmployees();
      alert('User unlinked successfully');
    } catch (error) {
      console.error('Error unlinking user:', error);
      alert(error.response?.data?.message || 'Failed to unlink user');
    }
  };

  if (loading && employees.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Employees</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage your organization's employees and their information
          </p>
        </div>
        {hasPermission(['admin', 'manager']) && (
          <button
            onClick={() => openModal('create')}
            className="btn-primary flex items-center space-x-2"
          >
            <UserPlusIcon className="w-4 h-4" />
            <span>Add Employee</span>
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="card">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                Search Employees
              </label>
              <div className="mt-1 relative">
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                  placeholder="Search by name, email, or employee number..."
                />
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>
            <div>
              <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                Department
              </label>
              <select
                id="department"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="form-select mt-1"
              >
                <option value="">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept.id || dept.name} value={dept.name}>
                    {dept.name} {dept.employee_count && `(${dept.employee_count})`}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button type="submit" className="btn-primary w-full">
                Search
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Employees Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {employees.map((employee) => (
          <div key={employee.id} className="card hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 font-semibold text-lg">
                    {employee.first_name?.charAt(0) || employee.last_name?.charAt(0) || 'E'}
                  </span>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">
                    {employee.first_name} {employee.last_name}
                  </h3>
                  <p className="text-sm text-gray-600">{employee.employee_number}</p>
                  <p className="text-xs text-gray-500">{employee.job_title || 'No job title'}</p>
                  <p className="text-xs text-gray-500">{employee.department || 'No department'}</p>
                </div>
              </div>
              
              <div className="flex space-x-1">
                <button
                  onClick={() => openModal('view', employee)}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="View Details"
                >
                  <EyeIcon className="w-4 h-4" />
                </button>
                {hasPermission(['admin', 'manager']) && (
                  <>
                    <button
                      onClick={() => openModal('edit', employee)}
                      className="p-2 text-gray-400 hover:text-blue-600"
                      title="Edit Employee"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeactivate(employee.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                      title="Deactivate Employee"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
            
            {/* Additional Employee Information */}
            <div className="mt-4 space-y-2">
              <div className="flex items-center text-xs text-gray-500">
                <EnvelopeIcon className="w-3 h-3 mr-1" />
                <span>{employee.email || employee.personal_email || 'No email'}</span>
              </div>
              <div className="flex items-center text-xs text-gray-500">
                <PhoneIcon className="w-3 h-3 mr-1" />
                <span>{employee.phone || employee.mobile || 'No phone'}</span>
              </div>
              <div className="flex items-center text-xs text-gray-500">
                <BuildingOfficeIcon className="w-3 h-3 mr-1" />
                <span>{employee.location || 'No location'}</span>
              </div>
              
              {/* Probation Information */}
              {employee.probation_end_date && new Date(employee.probation_end_date) > new Date() && (
                <div className="flex items-center text-xs text-orange-600">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Probation until {new Date(employee.probation_end_date).toLocaleDateString('en-GB')} ({employee.probation_months || 6} months)</span>
                </div>
              )}
              
              {/* User Association Section */}
              {hasPermission(['admin', 'hr_manager']) && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700">User Account:</span>
                    {employee.user_id ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-green-600 flex items-center">
                          <UserIcon className="w-3 h-3 mr-1" />
                          {employee.email}
                        </span>
                        <button
                          onClick={() => handleUnlinkUser(employee)}
                          className="text-xs text-red-500 hover:text-red-700"
                          title="Unlink user account"
                        >
                          Unlink
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => openUserModal(employee, 'link')}
                          className="text-xs text-blue-500 hover:text-blue-700 flex items-center"
                          title="Link existing user"
                        >
                          <LinkIcon className="w-3 h-3 mr-1" />
                          Link
                        </button>
                        <span className="text-xs text-gray-400">|</span>
                        <button
                          onClick={() => openUserModal(employee, 'create')}
                          className="text-xs text-green-500 hover:text-green-700 flex items-center"
                          title="Create new user account"
                        >
                          <UserPlusIcon className="w-3 h-3 mr-1" />
                          Create
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="flex justify-between items-center mt-3">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  employee.employment_status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : employee.employment_status === 'inactive'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {employee.employment_status?.toUpperCase()}
                </span>
                <span className="text-xs text-gray-500">
                  {employee.hire_date ? new Date(employee.hire_date).toLocaleDateString() : 'No hire date'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="btn-secondary disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="btn-secondary disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Employee Detail Modal */}
      {showModal && modalMode === "view" && (
        <EmployeeDetailModal
          employee={selectedEmployee}
          onClose={closeModal}
          onEdit={() => openModal("edit", selectedEmployee)}
        />
      )}
      
      {showModal && modalMode === "create" && (
        <EmployeeCreateModal
          onClose={closeModal}
          onSave={() => {
            fetchEmployees();
            closeModal();
          }}
        />
      )}
      

      {showModal && modalMode === "edit" && (
        <EmployeeEditModal
          employee={selectedEmployee}
          onClose={closeModal}
          onSave={() => {
            fetchEmployees();
            closeModal();
          }}
        />
      )}

      {/* User Association Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {userModalMode === 'create' ? 'Create User Account' : 'Link User Account'}
              </h3>
              <button
                onClick={closeUserModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {userMessage && (
              <div className={`mb-4 p-3 rounded ${
                userMessage.includes('successfully') 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                {userMessage}
              </div>
            )}

            {userModalEmployee && (
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <p className="font-medium text-gray-900">
                  {userModalEmployee.first_name} {userModalEmployee.last_name}
                </p>
                <p className="text-sm text-gray-600">
                  {userModalEmployee.employee_number} • {userModalEmployee.job_title}
                </p>
                <p className="text-sm text-gray-600">
                  {userModalEmployee.department}
                </p>
              </div>
            )}

            {userModalMode === 'create' ? (
              <form onSubmit={handleCreateNewUser}>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    required
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="user@company.com"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                    User Role
                  </label>
                  <select
                    id="role"
                    value={newUserData.role}
                    onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="employee">Employee</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeUserModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={userLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {userLoading ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </form>
            ) : (
              <div>
                {/* Auto-link option for matching email */}
                {userModalEmployee && (userModalEmployee.email || userModalEmployee.personal_email) && 
                 availableUsers.find(user => user.email.toLowerCase() === (userModalEmployee.email || userModalEmployee.personal_email).toLowerCase()) && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm font-medium text-blue-900 mb-2">Quick Link Available</p>
                    <p className="text-sm text-blue-700 mb-3">
                      Found a user account with matching email: {userModalEmployee.email || userModalEmployee.personal_email}
                    </p>
                    <button
                      onClick={handleAutoLinkByEmail}
                      disabled={userLoading}
                      className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {userLoading ? 'Linking...' : 'Auto-Link by Email'}
                    </button>
                  </div>
                )}

                <p className="mb-4 text-sm text-gray-600">
                  Or select an existing user to link with this employee:
                </p>
                <div className="max-h-64 overflow-y-auto mb-4">
                  {availableUsers.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No available users to link</p>
                  ) : (
                    <div className="space-y-2">
                      {availableUsers.map((user) => (
                        <div
                          key={user.id}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded hover:bg-gray-50"
                        >
                          <div>
                            <p className="font-medium text-gray-900">{user.email}</p>
                            <p className="text-sm text-gray-600">{user.role}</p>
                          </div>
                          <button
                            onClick={() => handleLinkExistingUser(user.id)}
                            disabled={userLoading}
                            className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            {userLoading ? 'Linking...' : 'Link'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={closeUserModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
